'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  FileText, LayoutList, Eye, ChevronLeft, ChevronRight, Plus, Zap
} from 'lucide-react';
import AppLogo from './ui/AppLogo';

const NAV_ITEMS = [
  { href: '/quotations-management', icon: LayoutList, label: 'All Quotations', key: 'nav-management' },
  { href: '/quotation-editor', icon: FileText, label: 'New Quotation', key: 'nav-editor' },
  { href: '/quotation-preview', icon: Eye, label: 'Preview', key: 'nav-preview' },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col bg-[#1B2A4A] border-r border-[#243556] transition-all duration-300 ease-in-out shrink-0 ${collapsed ? 'w-16' : 'w-56'}`}
      style={{ minHeight: '100vh' }}
    >
      {/* Logo */}
      <div className={`flex items-center gap-2 px-3 py-4 border-b border-[#243556] ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-white text-sm tracking-wide leading-tight">PEAKTIME</span>
            <span className="text-[10px] text-[#C9922A] font-medium tracking-widest uppercase leading-tight">Quote</span>
          </div>
        )}
      </div>
      {/* New Quote CTA */}
      {!collapsed && (
        <div className="px-3 pt-4">
          <Link
            href="/quotation-editor"
            className="flex items-center gap-2 w-full bg-[#E8620A] hover:bg-[#d05508] text-white rounded-lg px-3 py-2 text-sm font-600 transition-all duration-150 active:scale-95"
          >
            <Plus size={16} />
            <span className="font-semibold">New Quotation</span>
          </Link>
        </div>
      )}
      {collapsed && (
        <div className="px-2 pt-4">
          <Link
            href="/quotation-editor"
            title="New Quotation"
            className="flex items-center justify-center w-full bg-[#E8620A] hover:bg-[#d05508] text-white rounded-lg p-2 transition-all duration-150 active:scale-95"
          >
            <Plus size={18} />
          </Link>
        </div>
      )}
      {/* Nav */}
      <nav className="flex-1 px-2 pt-4 space-y-1">
        {NAV_ITEMS?.map(item => {
          const active = pathname === item?.href || (item?.href === '/quotation-editor' && pathname?.startsWith('/quotation-editor'));
          return (
            <Link
              key={item?.key}
              href={item?.href}
              title={collapsed ? item?.label : undefined}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 group ${
                active
                  ? 'bg-[#E8620A]/20 text-[#FF8C3A]'
                  : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span>{item?.label}</span>}
            </Link>
          );
        })}
      </nav>
      {/* Brand tagline */}
      {!collapsed && (
        <div className="px-3 py-3 border-t border-[#243556]">
          <div className="flex items-center gap-2 text-[#64748B]">
            <Zap size={12} className="text-[#C9922A]" />
            <span className="text-[10px] font-medium tracking-wide">www.peaktime.in</span>
          </div>
        </div>
      )}
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="absolute -right-3 top-20 bg-[#1B2A4A] border border-[#243556] rounded-full p-1 text-[#94A3B8] hover:text-white transition-colors z-10"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  );
}